import type { OwnershipMethod, Platform, SocialVerificationStatus } from './enums.js';

/**
 * The Connections rules both sides have to agree on, and the constants a
 * screen needs at runtime.
 *
 * NO ZOD HERE, ON PURPOSE — same reason as `campaigns.ts`. The request and
 * response schemas live in `./contracts.ts`; eagerly built zod objects do not
 * tree-shake, so a barrel that re-exported them would ship every server-side
 * rule to the browser. This file is plain types, plain constants and pure
 * functions, all of which the Connections screen genuinely uses.
 */

/* ------------------------------------------------------------------ */
/* The bio code                                                        */
/* ------------------------------------------------------------------ */

/** Every issued code starts with this, so a creator can find it in their bio. */
export const BIO_CODE_PREFIX = 'nx-';

/** Characters after the prefix. */
export const BIO_CODE_BODY_LENGTH = 6;

/**
 * The alphabet, with `0/O`, `1/I/L` and `U` left out.
 *
 * A creator reads this code off one screen and types it into another app, and
 * every pair above is the pair people get wrong. 30 characters to the power of
 * 6 is 7.3e8 codes, which is far more entropy than this needs — the code is not
 * a secret, it is a coincidence-proof marker that the person who asked for it
 * controls the bio. (A byte reduced modulo 30 is very slightly biased towards
 * the first sixteen symbols; written down rather than corrected, because what
 * this value has to be is un-collidable, not uniformly sampled.)
 */
export const BIO_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';

/** How long an issued code stays usable. */
export const BIO_CODE_TTL_HOURS = 24;

/**
 * Verify attempts allowed per hour.
 *
 * This is a quota limit, not a security one — the account is the creator's own.
 * Every attempt spends one provider read, and Instagram's plan is 15,000 reads
 * a *month* shared with Amplify's fleet sync, so a creator hammering Verify
 * while they edit their bio is a real cost. Three an hour is enough to fix a
 * typo twice.
 *
 * IT IS COUNTED PER CREATOR AS WELL AS PER CONNECTION. Counting only per
 * connection row looks right and is not: disconnecting and reconnecting makes
 * a NEW `SocialAccount` row, whose hourly count starts at zero, so a creator
 * who wanted to could spend the Instagram month in an afternoon by cycling the
 * button. The per-creator count is deliberately more generous, because a
 * creator legitimately has up to three platforms to prove.
 */
export const BIO_CODE_ATTEMPTS_PER_HOUR = 3;

/** Verify attempts allowed per CREATOR per hour, across every connection. */
export const BIO_CODE_ATTEMPTS_PER_CREATOR_PER_HOUR = 9;

/* ------------------------------------------------------------------ */
/* How many accounts, per platform                                     */
/* ------------------------------------------------------------------ */

/**
 * Live connections one creator may hold on one platform.
 *
 * Nexus started at one, enforced by a partial unique index on
 * `(creatorId, platform)`. Clippers do not work that way: one person runs
 * several accounts on the same platform and posts a different cut from each.
 * Five is the number the operator asked for, and it is a real limit rather
 * than a formality — the per-account provider reads (a profile refresh, a feed
 * read) are metered, Instagram's plan is 15,000 reads a MONTH, and an
 * unbounded list is an unbounded bill.
 *
 * It is enforced in the DATABASE, by the `SocialAccount_live_per_platform_*`
 * triggers migration 0009 creates, for the same reason the old rule was an
 * index: the API runs in many isolates and two of them can each see four
 * accounts at the same moment. The API checks first only so the creator gets a
 * sentence instead of `UNIQUE constraint failed`.
 */
export const MAX_LIVE_ACCOUNTS_PER_PLATFORM = 5;

/** What a creator is told when the platform is full. Shown verbatim. */
export function platformFullMessage(label: string): string {
  return `You have ${MAX_LIVE_ACCOUNTS_PER_PLATFORM} ${label} accounts connected, which is the limit. Disconnect one before connecting another.`;
}

/** "3 of 5 connected", on the Add-account control. */
export function connectedCountLabel(connected: number): string {
  return `${connected} of ${MAX_LIVE_ACCOUNTS_PER_PLATFORM} connected`;
}

/* ------------------------------------------------------------------ */
/* Account metrics                                                     */
/* ------------------------------------------------------------------ */

/**
 * How `SocialAccount.engagementRateBasisPoints` is stored, in one sentence:
 * BASIS POINTS, an integer, where 310 means 3.10%.
 *
 * An integer because the column is an integer and a float would round
 * differently in D1 and in the browser; basis points rather than per mille
 * because engagement rates on these platforms routinely sit under 1% and a
 * per-mille integer would collapse 0.04% and 0.09% onto the same value.
 *
 * NULL IS NOT ZERO here either. A null rate means it could not be computed —
 * too few posts, a feed the platform did not publish, a provider that was
 * unwell — and the screen renders it as an em dash with the reason, never as
 * "0%", which would say this creator's audience ignores them.
 */
export const ENGAGEMENT_RATE_SCALE = 10_000;

/** 310 -> "3.10%". Null stays null; the screen renders that as an em dash. */
export function formatEngagementRate(basisPoints: number | null): string | null {
  if (basisPoints === null) return null;
  return `${(basisPoints / 100).toFixed(2)}%`;
}

/**
 * Which package calls produced a row's metrics, stored in
 * `SocialAccount.metricsSource`.
 *
 * It is recorded because the two are not the same evidence: a `PROFILE` row
 * has follower / following / post counts and NO engagement rate, and a screen
 * that cannot tell that apart from "the rate is genuinely unknown" has no way
 * to say why the dash is there.
 */
export const ACCOUNT_METRICS_SOURCES = ['PROFILE', 'PROFILE_AND_RECENT_POSTS'] as const;
export type AccountMetricsSource = (typeof ACCOUNT_METRICS_SOURCES)[number];

/**
 * How often one account's metrics may be refreshed by hand.
 *
 * This is the TikTok and YouTube number. It is a quota limit, not a politeness
 * one: a refresh spends a profile read plus a feed read, and a follower count
 * does not move meaningfully inside a day.
 */
export const METRICS_REFRESH_COOLDOWN_HOURS = 24;

/**
 * Instagram's, which is a week, and the arithmetic that sets it.
 *
 * The plan is FIFTEEN THOUSAND RapidAPI requests a MONTH, and it is shared:
 * Amplify's fleet sync takes about 9,000 of them, so roughly 6,000 a month is
 * everything Nexus gets for every Instagram read it makes — proving ownership,
 * checking a submission, and the five scheduled readings each post needs
 * before anybody is paid.
 *
 * A daily refresh does not fit inside that. The pilot is ten creators who may
 * hold five Instagram accounts each: fifty accounts, two requests per refresh
 * (the profile, then the feed), which at once a day is 100 a day — 3,000 a
 * month, HALF of Nexus's whole allowance, spent on a number that decorates a
 * screen rather than on the reads that decide what a creator is paid. At once
 * a week the same worst case is about 430 a month, and the number on the
 * screen is at worst seven days old — which for a follower count and an
 * engagement rate over the last ten posts is not a different number.
 *
 * TikTok (1.5M a month) and YouTube (10,000 units a day) have no such problem
 * and keep the daily cooldown.
 */
export const INSTAGRAM_METRICS_REFRESH_COOLDOWN_HOURS = 168;

/** The cooldown for one platform's Refresh button. */
export function metricsRefreshCooldownHours(platform: Platform): number {
  return platform === 'INSTAGRAM'
    ? INSTAGRAM_METRICS_REFRESH_COOLDOWN_HOURS
    : METRICS_REFRESH_COOLDOWN_HOURS;
}


/**
 * Why there is no engagement rate, said to the creator.
 *
 * Every branch names something they can check, because "—" on its own reads as
 * a bug in Nexus rather than as a fact about a platform.
 */
export function engagementUnknownHint(source: AccountMetricsSource | null, label: string): string {
  if (source === null) {
    return `Not read yet. Press Refresh and we will read your ${label} profile once.`;
  }
  if (source === 'PROFILE') {
    return `We read your ${label} profile but could not read your recent posts, so there is no rate yet. Press Refresh to try again.`;
  }
  return `We read your recent ${label} posts and they do not publish enough likes, comments or views to work a rate out — at least three posts have to.`;
}

/* ------------------------------------------------------------------ */
/* Handles a platform never gave us                                    */
/* ------------------------------------------------------------------ */

/**
 * The marker for a connection whose handle the provider did not reveal.
 *
 * TikTok's `user.info.basic` does not carry the handle; it is recovered from
 * the `share_url` of one video, and a creator with no videos yet connects
 * successfully with no handle at all. The uniqueness indexes still need
 * SOMETHING in `normalizedHandle`, so it holds the provider's own id behind
 * this prefix.
 *
 * The prefix contains a colon ON PURPOSE: `normalizeHandle` only ever returns
 * `[a-z0-9._-]`, so no handle a creator can type will ever collide with one of
 * these, and nothing downstream can mistake it for a handle it could look up.
 * `isSyntheticHandle` is the test every public-profile read has to make first —
 * reading a profile called `oauth:tiktok:abc` gets a 404 from the provider,
 * which would otherwise be recorded as the creator's account not existing.
 */
export const SYNTHETIC_HANDLE_PREFIX = 'oauth:';

export function syntheticHandle(platform: Platform, platformAccountId: string): string {
  return `${SYNTHETIC_HANDLE_PREFIX}${platform.toLowerCase()}:${platformAccountId.toLowerCase()}`;
}

export function isSyntheticHandle(normalizedHandle: string): boolean {
  return normalizedHandle.startsWith(SYNTHETIC_HANDLE_PREFIX);
}

/** `nx-XXXXXX`, case-insensitive. */
export function isBioCode(value: string): boolean {
  const re = new RegExp(`^${BIO_CODE_PREFIX}[${BIO_CODE_ALPHABET}]{${BIO_CODE_BODY_LENGTH}}$`, 'i');
  return re.test(value.trim());
}

/**
 * Is the code in this bio?
 *
 * Case-insensitive, and tolerant of the characters platforms insert around a
 * pasted string — zero-width joiners, non-breaking spaces and the like — because
 * a creator who did exactly what they were asked must not be told they did not.
 * It is a containment test and not an equality test on purpose: the code sits
 * inside the bio the creator already had.
 */
export function bioContainsCode(bio: string, code: string): boolean {
  const flatten = (s: string): string =>
    s
      .normalize('NFKC')
      // Spaces and invisibles a bio editor can slip into a pasted string:
      // NBSP, the zero-width family, word joiner, and the BOM some clients
      // prepend. A creator who did exactly what they were asked must not be
      // told they did not.
      .replace(/[\s\u00a0\u180e\u200b-\u200f\u2060\ufeff]/g, '')
      .toLowerCase();
  return flatten(bio).includes(flatten(code));
}

/* ------------------------------------------------------------------ */
/* Handles                                                             */
/* ------------------------------------------------------------------ */

export const PLATFORM_LABELS: Readonly<Record<Platform, string>> = Object.freeze({
  TIKTOK: 'TikTok',
  INSTAGRAM: 'Instagram',
  YOUTUBE: 'YouTube',
});

/** What the handle field should say, per platform. */
export const PLATFORM_HANDLE_HINTS: Readonly<Record<Platform, string>> = Object.freeze({
  TIKTOK: 'Your @handle, or a link to your profile.',
  INSTAGRAM: 'Your @handle, or a link to your profile.',
  YOUTUBE: 'Your @handle — not a channel id and not the channel name.',
});

/**
 * A pasted profile URL or an `@handle`, reduced to the bare handle.
 *
 * Shared because `SocialAccount.normalizedHandle` carries a lower-case CHECK
 * and two unique indexes compare it literally: if the screen and the Worker
 * normalised differently, "this account is already connected" would be decided
 * by which of the two last touched the row.
 *
 * Returns null when there is no handle in the string at all. **A YouTube
 * `UC…` channel id is refused rather than normalised**: channel ids are
 * case-sensitive base64url, and this column is lower-cased, so accepting one
 * would let two different channels collide on the uniqueness index that is
 * supposed to stop one account being claimed by two creators.
 */
export function normalizeHandle(platform: Platform, raw: string): string | null {
  let value = raw.trim();
  if (!value) return null;

  /* Parsed with a regex rather than `URL`, because this package compiles with
     `lib: ES2022` and no DOM: it has to typecheck identically for the browser
     bundle and the Worker, and reaching for a DOM lib to get one constructor
     would open the door to browser-only APIs in code the Worker also runs. */
  const asUrl = /^(?:https?:\/\/)?(?:www\.|m\.)?(?:tiktok|instagram|youtube)\.com\/([^?#]*)/i.exec(
    value,
  );
  if (asUrl) {
    const segments = (asUrl[1] ?? '').split('/').filter(Boolean);
    const at = segments.find((s) => s.startsWith('@'));
    // `/channel/UC…` and `/c/Name` are not handles; the first segment of an
    // Instagram or TikTok profile URL is.
    value = at ?? (segments.length === 1 ? (segments[0] ?? '') : '');
  }

  value = value.replace(/^@+/, '').trim();
  if (!value) return null;
  if (platform === 'YOUTUBE' && /^UC[A-Za-z0-9_-]{10,}$/.test(value)) return null;
  if (!/^[A-Za-z0-9._-]{1,60}$/.test(value)) return null;
  return value.toLowerCase();
}

/**
 * The public profile page, for the link on the Connections card.
 *
 * Null when there is no handle to build one from — see `syntheticHandle`. A
 * link to `tiktok.com/@oauth:tiktok:abc` is a 404 with a creator's name on it.
 */
export function profileUrlFor(platform: Platform, handle: string): string | null {
  const h = handle.replace(/^@+/, '');
  if (isSyntheticHandle(h)) return null;
  switch (platform) {
    case 'TIKTOK':
      return `https://www.tiktok.com/@${h}`;
    case 'INSTAGRAM':
      return `https://www.instagram.com/${h}/`;
    case 'YOUTUBE':
      return `https://www.youtube.com/@${h}`;
  }
}

/* ------------------------------------------------------------------ */
/* How ownership was proven                                            */
/* ------------------------------------------------------------------ */

export const PROOF_METHOD_LABELS: Readonly<Record<OwnershipMethod, string>> = Object.freeze({
  OAUTH: 'Signed in with the platform',
  BIO_CODE: 'Code in the bio',
  MANUAL: 'Checked by the operator',
});

export const CONNECTION_STATUS_LABELS: Readonly<Record<SocialVerificationStatus, string>> =
  Object.freeze({
    PENDING: 'Not proven yet',
    CHALLENGE_ISSUED: 'Waiting for the code',
    VERIFIED: 'Connected',
    REJECTED: 'Refused',
    REVOKED: 'Disconnected',
  });

/**
 * The read-only scopes each platform's app asks for, named exactly as the
 * provider names them.
 *
 * They are here rather than only in the Worker because the Connections screen
 * tells the creator what it is about to ask for before it sends them, and
 * `docs/oauth-setup.md` has to list the same strings for the operator's app
 * registrations. One list, three readers.
 */
export const OAUTH_SCOPES: Readonly<Record<Platform, readonly string[]>> = Object.freeze({
  TIKTOK: ['user.info.basic', 'video.list'],
  INSTAGRAM: ['instagram_basic'],
  YOUTUBE: ['https://www.googleapis.com/auth/youtube.readonly'],
});

/** What the creator is told each grant lets Nexus see. */
export const OAUTH_SCOPE_EXPLANATIONS: Readonly<Record<Platform, string>> = Object.freeze({
  TIKTOK: 'Your account id and your list of videos. Nexus cannot post, edit or delete anything.',
  INSTAGRAM:
    'Your professional account id, handle and public profile. Nexus cannot post, edit or delete anything.',
  YOUTUBE: 'Your channel and its public statistics, read-only. Nexus cannot upload or change anything.',
});

/** Shown where a provider has no credentials configured yet. */
export const OAUTH_UNAVAILABLE_MESSAGE = 'Not yet available — use the bio code.';

/* ------------------------------------------------------------------ */
/* Total reach                                                         */
/* ------------------------------------------------------------------ */

/**
 * One account, as the reach sum needs it. Structural on purpose: the response
 * type lives in `./contracts.ts` and this file must not import it (zod would
 * follow it into the browser bundle), so the function asks for the two columns
 * it actually reads.
 */
export interface ReachAccount {
  followerCountAtConnect: number | null;
  metricsRefreshedAt: string | null;
}

export interface ReachPlatform {
  platform: Platform;
  label: string;
  accounts: ReachAccount[];
  connectedCount: number;
  limit: number;
}

export interface PlatformReach {
  platform: Platform;
  label: string;
  /** Followers on this platform, over the accounts that published a count. */
  followers: number;
  accounts: number;
  /** Accounts here whose follower count nobody has read yet. */
  unknown: number;
}

export interface ReachSummary {
  /** The headline: followers across every live account on every platform. */
  totalFollowers: number;
  byPlatform: PlatformReach[];
  accountsConnected: number;
  /** Three platforms times five accounts. What "n of 15" counts against. */
  accountLimit: number;
  /** The most recent profile read across every account, or null for none. */
  lastRefreshedAt: string | null;
  /**
   * How many connected accounts contribute NOTHING to the figure above.
   *
   * A null follower count is not a zero — the platform did not publish one, or
   * nothing has read the profile yet — so it is excluded from the sum and
   * counted here instead. The strip says so, because a total that silently
   * dropped two of a creator's five accounts is a total they cannot check.
   */
  accountsWithoutFollowers: number;
}

/**
 * Total reach: the sum of followers across every LIVE connected account.
 *
 * "Live" is whatever is in the platform sections — pending, challenged and
 * verified — and deliberately the same population as the "n of 15" beside it:
 * two figures on one strip counting two different sets of accounts is the
 * quickest way to make a reader distrust both.
 *
 * NULL IS NOT ZERO, here as everywhere else on this screen. An account whose
 * follower count has not been read adds nothing to the sum and is counted in
 * `accountsWithoutFollowers`, so the strip can say how much of the creator's
 * estate the headline actually covers rather than quietly under-reporting it.
 */
export function reachSummary(platforms: readonly ReachPlatform[]): ReachSummary {
  let totalFollowers = 0;
  let accountsConnected = 0;
  let accountLimit = 0;
  let accountsWithoutFollowers = 0;
  let lastRefreshedAt: string | null = null;

  const byPlatform = platforms.map((section) => {
    let followers = 0;
    let unknown = 0;
    for (const account of section.accounts) {
      if (typeof account.followerCountAtConnect === 'number') {
        followers += account.followerCountAtConnect;
      } else {
        unknown += 1;
      }
      /* ISO-8601 UTC compares lexicographically, which is why this is a string
         comparison and not two `Date.parse` calls per account. */
      if (
        account.metricsRefreshedAt &&
        (lastRefreshedAt === null || account.metricsRefreshedAt > lastRefreshedAt)
      ) {
        lastRefreshedAt = account.metricsRefreshedAt;
      }
    }
    totalFollowers += followers;
    accountsConnected += section.connectedCount;
    accountLimit += section.limit;
    accountsWithoutFollowers += unknown;
    return {
      platform: section.platform,
      label: section.label,
      followers,
      accounts: section.connectedCount,
      unknown,
    };
  });

  return {
    totalFollowers,
    byPlatform,
    accountsConnected,
    accountLimit,
    lastRefreshedAt,
    accountsWithoutFollowers,
  };
}

/**
 * A big number, shortened: 1,240 -> "1.2K", 4,210,000 -> "4.2M".
 *
 * Only for the ONE headline figure on Connections. Everything else on that
 * screen prints the exact number through `<Num>`, because a follower count a
 * creator compares against the platform's own app has to match it to the
 * digit; the hero figure is a scale, is read at 2rem, and carries the exact
 * value in its `title`.
 */
export function compactCount(value: number): string {
  const abs = Math.abs(value);
  if (abs < 1_000) return value.toLocaleString('en-US');
  const [divisor, suffix] =
    abs >= 1_000_000_000
      ? [1_000_000_000, 'B']
      : abs >= 1_000_000
        ? [1_000_000, 'M']
        : [1_000, 'K'];
  const scaled = value / divisor!;
  /* One decimal under 100 of a unit ("1.2K", "12.4M") and none above it
     ("124K"), which is where the decimal stops carrying information. */
  const text = Math.abs(scaled) >= 100 ? Math.round(scaled).toString() : scaled.toFixed(1);
  return `${text.replace(/\.0$/, '')}${suffix}`;
}
